import { computed, type ComputedRef } from 'vue'
import type { DataColumn, DataError, DataProvider, DataRow } from '@hantera/design-system'
import type { TracesViewContext } from './traces-view-context'

import type { TraceSummary } from '@/backend/traces.types'

/**
 * Human-readable elapsed time.
 *
 * `elapsedMs` is wall-clock between the first and last span, so a trace containing a job scheduled
 * for next month legitimately spans months. Rendering that as "8121600000 ms" reads as a bug, so
 * the unit follows the magnitude.
 */
export function formatElapsed(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)} ms`

  const seconds = ms / 1000
  if (seconds < 60) return `${seconds.toFixed(seconds < 10 ? 1 : 0)} s`

  const minutes = seconds / 60
  if (minutes < 60) return `${Math.round(minutes)} min`

  const hours = minutes / 60
  if (hours < 48) return `${Math.round(hours)} h`

  return `${Math.round(hours / 24)} days`
}

export function formatEntryPoint(trace: TraceSummary): string {
  // Nullable: a trace whose root span expired has no entry point to name.
  if (!trace.entryPoint) return '—'

  // The resource path, not `kind resourceId`: it is what the search box accepts, so an operator can
  // copy it straight out of the column and paste it back to filter on just this trace's entry point.
  return trace.entryPoint.spanPath
}

export class TracesDataProvider implements DataProvider {
  public readonly rows: ComputedRef<DataRow[]>
  public readonly isLoading: ComputedRef<boolean>
  public readonly allLoaded: ComputedRef<boolean>
  public readonly errors: ComputedRef<DataError[] | undefined>
  public readonly columns: ComputedRef<DataColumn[]>
  public readonly totalCount: ComputedRef<{ count: number; cap?: number } | undefined>

  constructor(
    private context: TracesViewContext,
    private t: (key: string, params?: Record<string, any>) => string,
  ) {
    this.isLoading = computed(() => this.context.listState.isLoading)

    /**
     * `DataTable` re-invokes `loadMore()` whenever the row count changes and this is false, so this
     * must latch true on the last page or the table becomes an unbounded request loop. The context
     * takes `hasMore` from the server's response rather than comparing the row count to the page
     * size, which cannot tell an exactly-full page from a truncated one.
     */
    this.allLoaded = computed(() => !this.context.hasMore)


    this.errors = computed(() => {
      const error = this.context.listState.error
      return error ? [{ code: 'LOAD_ERROR', message: error }] : undefined
    })


    this.rows = computed(() =>
      this.context.items.map((trace) => ({
        key: trace.traceId,

        // Failure colours the row; a partial trace is incomplete rather than broken, so it is
        // merely noted in the status column.
        hue: trace.status === 'failed' ? '--color-hue-red' : undefined,
        values: [
          trace.startedAt,
          formatEntryPoint(trace),
          trace.spanCount,
          formatElapsed(trace.elapsedMs),
          this.formatStatus(trace),
        ],
      })) as DataRow[],
    )

    this.columns = computed<DataColumn[]>(() => [
      {
        key: 'startedAt',
        label: this.t('traces.columns.started'),
        valueIdx: 0,
        canSort: false,
        canFilter: false,
        values: { type: 'instant' },
      },
      {
        key: 'entryPoint',
        label: this.t('traces.columns.entryPoint'),
        valueIdx: 1,
        canSort: false,
        canFilter: false,
        values: { type: 'text', isArray: false },
      },
      {
        key: 'spanCount',
        label: this.t('traces.columns.spans'),
        valueIdx: 2,
        canSort: false,
        canFilter: false,
        values: { type: 'number', maximumFractionDigits: 0 },
      },
      {
        key: 'elapsed',
        label: this.t('traces.columns.elapsed'),
        valueIdx: 3,
        canSort: false,
        canFilter: false,
        values: { type: 'text', isArray: false },
      },
      {
        key: 'status',
        label: this.t('traces.columns.status'),
        valueIdx: 4,
        canSort: false,
        canFilter: false,
        values: { type: 'text', isArray: false },
      },
    ])

    // `cap` renders the count as "N+", which is all that can be claimed while pages remain: the
    // total is unknown without a count query the trace list deliberately does not run.
    this.totalCount = computed(() => {
      const count = this.context.items.length
      if (count === 0) return undefined
      return this.context.hasMore ? { count, cap: count } : { count }
    })


  }

  private formatStatus(trace: TraceSummary): string {
    if (trace.status === 'failed') {
      return this.t('traces.status.failed', { count: trace.failedCount })
    }
    return this.t(`traces.status.${trace.status}`)
  }

  public getRowHue(row: DataRow) {
    return (row as any).hue
  }

  public async refresh(): Promise<void> {
    await this.context.refresh()
  }

  public async loadMore(): Promise<void> {
    await this.context.loadMore()
  }

}
